import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useLandingPageCategoryQuery } from "../../../graphql/projects.hooks";
import { useTranslation } from "react-i18next";

import BlankLines from "./img/blank-lines.svg";
import { useUserQuery } from "../../../graphql/menu.hooks";
import { useNavigate } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { isShowTemplateModal, isUrlUniqueError } from "../../../graphql/state";
import {
  useDuplicateContentPageForCommonPageMutation,
  useDuplicatePageForCommonPageMutation,
} from "../../../graphql/page.hooks";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { Error } from "../../../utils";
import ReactSelect from "../../inputs/select";
import { PAGE_TYPE, SelectOption } from "../../../types";
import { GraphPageLanding } from "../../../../graphql/types";

interface TemplateModalProps {
  showModal: boolean;
  handleCloseModal: () => void;
}

interface TemplateType extends GraphPageLanding {
  isEmpty?: boolean;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  showModal,
  handleCloseModal,
}) => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const navigate = useNavigate();

  const { data, loading, refetch } = useLandingPageCategoryQuery();
  const templates = useMemo(
    () => data?.landingPageCategory || [],
    [data?.landingPageCategory]
  );
  const { refetch: userRefetch } = useUserQuery({
    fetchPolicy: "network-only",
  });
  const [duplicatePage, { loading: duplicateLoading }] =
    useDuplicatePageForCommonPageMutation();
  const [duplicateContent, { loading: duplicateContentLoading }] =
    useDuplicateContentPageForCommonPageMutation();
  const templateModal = useReactiveVar(isShowTemplateModal);

  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedSize, setSelectedSize] = useState<PAGE_TYPE>(PAGE_TYPE.BOTH);

  const [activeTemplate, setActiveTemplate] = useState<TemplateType | null>(
    null
  );
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);
  const sizeOptions: SelectOption[] = [
    {
      label: t("modal.Desktop & Mobile"),
      value: PAGE_TYPE.BOTH,
    },
    {
      label: t("modal.Desktop"),
      value: PAGE_TYPE.DESKTOP,
    },
    {
      label: t("modal.Mobile"),
      value: PAGE_TYPE.MOBILE,
    },
  ];

  useEffect(() => {
    if (!templates) return;
    setCategoryOptions(
      templates.map((item) => ({
        value: item.id,
        label: item.title,
      }))
    );
  }, [templates]);

  const handleCategoryChange = (event: any) => {
    setSelectedCategory(event.map((category: SelectOption) => category.label));
    setActiveTemplate(null);
  };

  const handleSizeChange = (event: any) => {
    setSelectedSize(+event.value);
    setActiveTemplate(null);
  };

  const handleTemplateClick = (template: TemplateType) => {
    setActiveTemplate(template);
  };

  const ME_PAGE_LINK = process.env.REACT_APP_ME_PAGE_URL;

  const navigateToPage = () => {
    if (templateModal?.page?.isCreated) {
      navigate(
        templateModal.page.folderId
          ? `${lng}/projects/${templateModal.page.domain.id}/folder/${templateModal.page.folder.id}`
          : `${lng}/projects/${templateModal.page.domain.id}`
      );
    } else {
      navigate(
        templateModal.page.folderId
          ? `${lng}/projects/${templateModal.page.domainId}/folder/${templateModal.page.folderId}`
          : `${lng}/projects/${templateModal.page.domainId}`
      );
    }
  };

  const closeModal = () => {
    if (templateModal?.page?.isCreated) {
      navigateToPage();
    }
    handleCloseModal();
  };

  const handleCustomizeClick = async () => {
    let newTab: Window | null = null;

    if (/(iPhone|iPad|iPod)/i.test(navigator.userAgent)) {
      newTab = window.open("about:blank", "_blank");
    }

    if (activeTemplate?.isEmpty && templateModal.page?.id) {
      const constructorUrl = process.env.REACT_APP_CONSTRUCTOR_URL + lng;
      const url = `${constructorUrl}/${templateModal.page?.id}${activeTemplate.fieldMode ? `/form` : ""}`;
      navigateToPage();
      isShowTemplateModal({ isActive: false, page: null });
      newTab ? (newTab.location.href = url) : window.open(url, "_blank");
      return;
    }
    if (templateModal.page?.isCreated) {
      try {
        const { data } = await duplicateContent({
          variables: {
            idPageFrom: activeTemplate.id,
            idPageTo: templateModal.page.id,
          },
        });

        if (data?.duplicateContentPageForCommonPage) {
          const constructorUrl = process.env.REACT_APP_CONSTRUCTOR_URL + lng;
          const newPageId = data?.duplicateContentPageForCommonPage.id;
          const url = `${constructorUrl}/${newPageId}${activeTemplate.fieldMode ? `/form` : ""}`;
          isShowTemplateModal({ isActive: false, page: null });
          newTab ? (newTab.location.href = url) : window.open(url, "_blank");
          navigateToPage();
        }
      } catch (error) {
        console.error(error);
      }
      return;
    }
    if (activeTemplate?.id !== 1 && !activeTemplate?.isEmpty) {
      try {
        const { data } = await duplicatePage({
          variables: { ...templateModal.page, id: activeTemplate.id },
        });
        if (data?.duplicatePageForCommonPage) {
          const constructorUrl = process.env.REACT_APP_CONSTRUCTOR_URL + lng;
          const newPageId = data?.duplicatePageForCommonPage.id;
          const url = `${constructorUrl}/${newPageId}${activeTemplate.fieldMode ? `/form` : ""}`;
          newTab ? (newTab.location.href = url) : window.open(url, "_blank");
          navigateToPage();
        }
      } catch (error) {
        if (error.message === Error.UNIQUE_URL) {
          isUrlUniqueError(true);
        }
      } finally {
        isShowTemplateModal({ isActive: false, page: null });
      }
    }
    await userRefetch();
    if (activeTemplate?.link) {
      closeModal();
      refetch();
      if (activeTemplate?.isEmpty) {
        if (newTab) {
          newTab.location.href = activeTemplate.link;
        } else {
          window.location.href = activeTemplate.link;
        }
      }
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      className="px-sm-2 px-md-0"
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title>{t("projects.selectPageTemplate")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Spinner animation="border" role="status"></Spinner>
        ) : (
          <>
            <div className="col-12 row g-2 g-md-3">
              <ReactSelect
                className="col-md-6 ps-1"
                options={categoryOptions}
                value={categoryOptions.find(
                  (category) => category.label === selectedCategory
                )}
                onChange={handleCategoryChange}
                isMulti
                placeholder={t("modal.All Categories")}
                isLoading={loading}
                label={t("modal.Select Categories")}
              />
              <ReactSelect
                className="col-md-6 ps-1"
                options={sizeOptions}
                value={sizeOptions.find((size) => size.value === selectedSize)}
                onChange={handleSizeChange}
                placeholder={t("modal.Desktop & Mobile")}
                label={t("modal.Template Size")}
              />
            </div>
            <div className="modal-category-inner custom-scrollbar mb-4 mt-3">
              {templates.map((templateCategory) => {
                if (
                  selectedCategory.includes(templateCategory.title) ||
                  selectedCategory.includes("all") ||
                  selectedCategory.length === 0
                ) {
                  return (
                    <div
                      key={templateCategory.id}
                      className="mb-4"
                      id="category-block"
                    >
                      <h2 className="fs-5 mb-2 text-dark-gray">
                        {templateCategory.title}
                      </h2>
                      <div className="row px-3 gap-3">
                        <div
                          className={`template-modal-item rounded-6 p-0 d-flex justify-content-center ${activeTemplate?.id === templateCategory.id ? "active" : ""}`}
                          onClick={() =>
                            handleTemplateClick({
                              id: templateCategory.id,
                              link: `${ME_PAGE_LINK}/page/create_default`,
                              fieldMode: false,
                              image: "",
                              isEmpty: true,
                            })
                          }
                          onDoubleClick={() => {
                            window.gtag?.("event", "customize_template");
                            handleCustomizeClick();
                          }}
                        >
                          <a
                            href="#"
                            className="h-100 d-flex justify-content-center p-3 text-decoration-none position-relative"
                            id="page-link-emty"
                          >
                            <p className="fs-6 fw-bold text-gray position-absolute top-50 start-50 translate-middle m-0 text-center text-nowrap">
                              {t("page.Blank Page")}
                            </p>
                            <img
                              className="img-fluid"
                              src={BlankLines}
                              alt="lines"
                            />
                          </a>
                        </div>
                        {templateCategory.pages
                          .filter(
                            (page) =>
                              selectedSize === PAGE_TYPE.BOTH ||
                              page.type === selectedSize
                          )
                          .map((page) => (
                            <div
                              key={page.id}
                              className={`template-modal-item rounded-6 d-flex justify-content-center ${activeTemplate?.id === page.id ? "active" : ""}`}
                              onClick={() => handleTemplateClick(page)}
                              onDoubleClick={() => {
                                window.gtag?.("event", "customize_template");
                                handleCustomizeClick();
                              }}
                            >
                              <a
                                href="#"
                                className="h-100 d-flex justify-content-center"
                                id="page-link"
                              >
                                <img
                                  className="img-fluid"
                                  src={page.image}
                                  alt={`Template ${page.id}`}
                                />
                              </a>
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
            <div className=" ms-lg-auto col-lg-4">
              <button
                className="btn btn-primary fw-bold text-white w-100"
                id="customizeButton"
                disabled={
                  !activeTemplate || duplicateLoading || duplicateContentLoading
                }
                onClick={() => {
                  window.gtag?.("event", "customize_template");
                  handleCustomizeClick();
                }}
              >
                {t("projects.customize")}
                {(duplicateLoading || duplicateContentLoading) && (
                  <Spinner
                    className="ms-3"
                    animation="border"
                    role="status"
                  ></Spinner>
                )}
              </button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TemplateModal;
